import { Box } from '@/components/box/box';
import { CapsizedText as Text } from '@/components/text/text';
import { ResizePlugin } from '@/lib/keen-resize-plugin';
import { styled } from '@/stitches.config';
import { useKeenSlider } from 'keen-slider/react';
import { useState } from 'react';
import { MobileSlide } from './mobile-slide';
import { useCaseTransitionStore } from '@/hooks/use-case-transition-store';
import { createTreemapFromPalette } from '@/utils/create-treemap-from-palette';
import { WorkSubsection } from '@/lib/schemas/work-subsection';

const SLIDES_PER_VIEW = 1.5;
const SLIDES_SPACING = 16;

interface MobileCardsProps {
  subsection: WorkSubsection;
  canAnimate: boolean;
}

export function MobileOverview({ subsection, canAnimate }: MobileCardsProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const { setCaseTransitionProps } = useCaseTransitionStore();

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
      slides: {
        perView: SLIDES_PER_VIEW,
        spacing: SLIDES_SPACING,
      },
      selector: '.first > .keen-slider__slide',
      mode: 'snap', // now using snap instead of free-snap as I felt it worked a bit better in this case
      loop: false,
    },
    [ResizePlugin]
  );

  const hasOneProject = subsection.projects.length === 1;

  return (
    <Box
      css={{
        display: 'contents',
        '@bp4': {
          display: 'none',
        },
      }}
    >
      <Box css={{ mt: '$6' }}>
        {!hasOneProject && slider.current && loaded && (
          <Text
            size={2}
            css={{
              textAlign: 'right',
              marginRight: '$4',
            }}
          >
            {currentSlide + 1} / {slider.current.track.details.slides.length}
          </Text>
        )}

        {hasOneProject ? (
          <Box
            css={{
              position: 'relative',
              width: `calc(${100 / SLIDES_PER_VIEW}% - ${SLIDES_SPACING / 2}px)`,
              aspectRatio: '1 / 1',
              '@supports not (aspect-ratio: 1 / 1)': {
                paddingBottom: '100%',
              },
            }}
          >
            <SliderAnchor
              href={`/project/${subsection.projects[0].target.slug.current}`}
              key={subsection.projects[0]._key}
              onClick={(event) => {
                event.preventDefault();
                const treemap = createTreemapFromPalette(
                  subsection.projects[0].target.cover.asset.metadata.palette
                );
                setCaseTransitionProps(
                  treemap,
                  `/project/${subsection.projects[0].target.slug.current}`
                );
              }}
            >
              <FloatingImageSlideContainer>
                <MobileSlide
                  project={subsection.projects[0]}
                  canAnimate={canAnimate}
                />
              </FloatingImageSlideContainer>
            </SliderAnchor>
          </Box>
        ) : (
          <div ref={sliderRef} className="keen-slider first" key="slider">
            {subsection.projects.map((project, index) => (
              <SliderAnchor
                href={`/project/${project.target.slug.current}`}
                key={project._key}
                className={`keen-slider__slide number-slide${index + 1}`}
                onClick={(event) => {
                  event.preventDefault();
                  const treemap = createTreemapFromPalette(
                    project.target.cover.asset.metadata.palette
                  );
                  setCaseTransitionProps(
                    treemap,
                    `/project/${project.target.slug.current}`
                  );
                }}
              >
                <MobileSlide project={project} canAnimate={canAnimate} />
              </SliderAnchor>
            ))}
          </div>
        )}
      </Box>
    </Box>
  );
}

const SliderAnchor = styled('a', {
  textDecoration: 'none',
  color: '$foreground',
});

const FloatingImageSlideContainer = styled('div', {
  textDecoration: 'none',
  color: '$foreground',
  '@supports not (aspect-ratio: 1 / 1)': {
    position: 'absolute',
    inset: '0px',
  },
});
